import { useEffect, useState } from 'react';

// Theme is a pure client concern — stored in localStorage, no server roundtrip.
// The old implementation read from user.attributes.theme + PUT /api/users/:id
// to toggle; the server PUT only accepts {role, name, phone, group_ids} and
// returns {ok: true}, so dispatching the response back into sessionActions
// wiped the entire user object (text disappeared, admin-gated UI collapsed).

const STORAGE_KEY = 'trackizy_theme';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch { /* localStorage unavailable */ }
  return 'system';
}

export function setStoredTheme(value) {
  try {
    if (value === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, value);
  } catch { /* localStorage unavailable */ }
  // Nudge listeners in the same tab; `storage` only fires cross-tab.
  window.dispatchEvent(new CustomEvent('trackizy-theme-change'));
}

export default function useTheme() {
  const [preference, setPreference] = useState(readStored);

  useEffect(() => {
    const onChange = () => setPreference(readStored());
    window.addEventListener('storage', onChange);
    window.addEventListener('trackizy-theme-change', onChange);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener('trackizy-theme-change', onChange);
    };
  }, []);

  const getSystemDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = preference === 'dark' || (preference === 'system' && getSystemDark());

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) root.classList.add('dark');
    else root.classList.remove('dark');
  }, [isDark]);

  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (mq.matches) document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  return { theme: preference, isDark, setTheme: setStoredTheme };
}
